import { isProfile, type Profile, type PullReview } from '@vivicta/asdp-contract';

/** The label of the T11 profile-change record issues (gate-review spec, PRF-10). */
export const PROFILE_CHANGE_LABEL = 'asdp-profile-change';

export type RecordIssue = {
  number: number;
  title: string;
  body: string | null;
  html_url: string;
  created_at: string;
  user: { login: string } | null;
};

export type ProfileChange = {
  issue: number;
  url: string;
  from?: Profile;
  to?: Profile;
  by?: string;
  reason?: string;
  at: string;
};

export type QuickStartChange = {
  number: number;
  title: string;
  author?: string;
  mergedAt: string;
  url: string;
};

export type { PullReview };

/** A "Field: value" line, or a "| Field | Value |" table row as the backend writes it. */
const fieldOfLine = (line: string): [string, string] | undefined => {
  const cells = line.split(/(?<!\\)\|/).map(cell => cell.trim());
  if (line.trim().startsWith('|') && cells.length >= 4) return [cells[1], cells[2]];
  const trimmed = line.replace(/^[\s*-]+/, '');
  const colon = trimmed.indexOf(':');
  return colon > 0 ? [trimmed.slice(0, colon), trimmed.slice(colon + 1)] : undefined;
};

const recordField = (body: string, name: string) => {
  for (const line of body.split(/\r?\n/)) {
    const field = fieldOfLine(line);
    if (field && field[0].replaceAll('*', '').trim().toLowerCase() === name) {
      return field[1].replaceAll('*', '').replaceAll(String.raw`\|`, '|').trim();
    }
  }
  return undefined;
};

const profileIn = (value?: string) => {
  const token = value?.split(/\s/)[0];
  return isProfile(token) ? token : undefined;
};

/**
 * One profile-change record issue. The T11 fields (from, to, by, reason) are
 * read from the record table (or "Field: value" lines); the title "P1 → P2" is the fallback.
 */
export function parseProfileChange(issue: RecordIssue): ProfileChange {
  const body = issue.body ?? '';
  const fromTitle = /\b(P[0-3])\b\D+\b(P[0-3])\b/.exec(issue.title);
  const by = recordField(body, 'by') ?? issue.user?.login;
  return {
    issue: issue.number,
    url: issue.html_url,
    from: profileIn(recordField(body, 'from')) ?? profileIn(fromTitle?.[1]),
    to: profileIn(recordField(body, 'to')) ?? profileIn(fromTitle?.[2]),
    by: by?.replace(/^@/, ''),
    reason: recordField(body, 'reason'),
    at: issue.created_at,
  };
}
